import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../utils/client";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { Link, useParams } from "react-router-dom";
import SponsorCarousel from "../components/SponsorCarousel";
import NewsComments from "../components/NewsComments";

const News = () => {
  const [newsItems, setNewsItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState([]);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [reload, setReload] = useState(false);
  const { newsId } = useParams();

  useEffect(() => {
    setIsLoadingPage(true);
    if (newsId) {
      const request = async () => {
        const response = await client.get(`/news/${newsId}`);
        setNewsItems([response.data]);
      };
      request().then(() => setIsLoadingPage(false));
    } else {
      const request = async () => {
        const response = await client.get(`/news?page=${currentPage}`);
        setNewsItems(response.data.newsItems);
        setCurrentPage(response.data.currentPage);
        setTotalPages(
          Array.from(
            { length: response.data.totalPages },
            (_, index) => index + 1
          )
        );
      };
      request().then(() => setIsLoadingPage(false));
    }
  }, [currentPage, newsId, reload]);

  return (
    <Layout>
      <Box maxW="1400px" mx="auto" px={[4, 6, 8]} py={[6, 10]}>
        <Flex alignItems="flex-start" flexDir={["column", "column", "row"]} gap={8}>
          {/* Main content */}
          <Box flex={1} minW={0}>
            <Skeleton isLoaded={!isLoadingPage}>
              <Heading
                fontSize={["1.6rem", "2rem"]}
                color="green.700"
                mb={1}
                borderLeft="4px solid"
                borderColor="green.400"
                pl={4}
              >
                Notícias
              </Heading>
              <Box w="60px" h="3px" bg="green.400" ml={4} mb={8} borderRadius="2px" />

              <Flex flexDir="column" gap={8}>
                {newsItems[0] &&
                  newsItems.map((news) => (
                    <Box
                      key={news.newsId}
                      bg="white"
                      borderRadius="1.25rem"
                      boxShadow="0 2px 12px rgba(0,0,0,0.07)"
                      border="1px solid"
                      borderColor="gray.100"
                      overflow="hidden"
                      p={[4, 6]}
                    >
                      <Link to={`./${news.newsId}`}>
                        <Heading
                          fontSize={["1.3rem", "1.6rem"]}
                          color="gray.800"
                          _hover={{ color: "green.600" }}
                          transition="color 0.2s"
                          mb={2}
                        >
                          {news.title}
                        </Heading>
                      </Link>
                      <Text fontSize="0.82rem" color="gray.400" mb={4}>
                        {news.authorName} &mdash;{" "}
                        {format(new Date(news.createdAt), "dd/MM/yyyy")}
                      </Text>
                      <Text
                        dangerouslySetInnerHTML={{ __html: news.text }}
                        color="gray.700"
                        lineHeight={1.8}
                        fontSize="1rem"
                      />
                      {news.images?.length > 0 && (
                        <Flex gap={3} wrap="wrap" py={5}>
                          {news.images.map((img) => (
                            <Image
                              key={img}
                              borderRadius="0.75rem"
                              maxH="350px"
                              objectFit="cover"
                              boxShadow="sm"
                              src={`${process.env.REACT_APP_S3_BASEURL}/news_images/${img}`}
                            />
                          ))}
                        </Flex>
                      )}
                      <NewsComments
                        comments={news.comments || []}
                        newsId={news.newsId}
                        reload={reload}
                        setReload={setReload}
                        preview={!newsId}
                      />
                    </Box>
                  ))}
              </Flex>

              <Flex gap={2} mt={8} alignItems="center" justifyContent="center">
                {totalPages[0] &&
                  totalPages.map((page) => (
                    page === currentPage ? (
                      <Box
                        key={page}
                        w="36px"
                        h="36px"
                        borderRadius="50%"
                        bg="green.500"
                        color="white"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight={700}
                        fontSize="0.9rem"
                      >
                        {page}
                      </Box>
                    ) : (
                      <Button
                        key={page}
                        colorScheme="green"
                        size="sm"
                        variant="outline"
                        borderRadius="50%"
                        w="36px"
                        h="36px"
                        p={0}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    )
                  ))}
              </Flex>
            </Skeleton>
          </Box>

          {/* Sidebar */}
          <Flex
            flexDir="column"
            w={["100%", "100%", "260px"]}
            flexShrink={0}
            gap={6}
          >
            <SponsorCarousel type="Principal" />
            <SponsorCarousel type="Secundário" />
          </Flex>
        </Flex>
      </Box>
    </Layout>
  );
};

export default News;
